import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function RemoveBrandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState({});

  useEffect(() => {
    let mounted = true;
    api.get('/dashboard/packages')
      .then((res) => {
        if (!mounted) return;
        setAccess(res.data?.access || {});
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load branding settings');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const removed = !!access?.remove_branding_allowed;
  const channels = [
    'Checkout',
    'Store',
    'Invoices',
    'Payment Links',
    'vCard',
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Remove Branding</h1>
          <p className="text-slate-500 text-sm mt-0.5">See whether JJuma branding is removed across your public pages and checkout flows.</p>
        </div>
        <Badge className={removed ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
          {removed ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Branding Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <p className="text-sm text-slate-600">
              {removed
                ? 'JJuma branding is removed from the eligible channels in your package.'
                : 'JJuma branding is currently required. Upgrade your package to remove it.'}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {channels.map((channel) => (
              <div key={channel} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3">
                <span className="text-sm text-slate-700">{channel}</span>
                <Badge className={removed ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                  {removed ? 'No branding' : 'Branded'}
                </Badge>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              <Sparkles size={14} className="mr-1" /> View Package
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/vcard')} className="border-slate-200 text-slate-700">
              <ShieldCheck size={14} className="mr-1" /> Check vCard Branding
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
